import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import type { S3ConfigViewModel } from '../api';

export function DeleteS3ConfigConfirmDialog({
  row,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly row: S3ConfigViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (row: S3ConfigViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('routes.admin.s3.configs.delete.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('routes.admin.s3.configs.delete.message', { name: row?.name ?? '' })}
        </p>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.s3.configs.form.errors.submit', { code: getErrorCodeForUi(submitError) })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.admin.s3.configs.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (row !== null) onConfirm(row);
            }}
          >
            {t('routes.admin.s3.configs.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
